import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { WarningBanner, UsageLimitBanner } from '../components/ui/warning-banner';
import { PlanChangeValidationModal, PlanChangeValidation } from '../components/subscription/PlanChangeValidationModal';
import {
  CreditCard,
  TrendingUp,
  Calendar,
  Zap,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Crown,
  Sparkles,
  BarChart3,
  Receipt,
  Settings,
  ArrowUpCircle,
  ArrowDownCircle,
  Star,
  DollarSign,
  Plug,
  Gift,
  Flame
} from 'lucide-react';
import { subscriptionService, CurrentSubscription, SubscriptionPlan, UsageDetails, Invoice } from '../services/subscription';
import { useOrganization } from '../contexts/OrganizationContext';

// Promotional offer configuration
const PROMO_CONFIG = {
  enabled: true,
  bonusMultiplier: 2, // 2x = 100% extra credits
  title: "Limited Time Offer",
  description: "Get 100% extra credits on all plans!",
  badge: "2X CREDITS",
  endDate: null as Date | null, // Set to a date to show countdown, null for no end date
};

export const BillingPage: React.FC = () => {
  const { hasPermission, isOrgAdmin } = useOrganization();
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [usageDetails, setUsageDetails] = useState<UsageDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [planFilter, setPlanFilter] = useState('all');
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    validation: PlanChangeValidation | null;
    targetPlanId: string | null;
    targetPlanName: string | null;
  }>({
    isOpen: false,
    validation: null,
    targetPlanId: null,
    targetPlanName: null,
  });
  const [billingPortalConfirm, setBillingPortalConfirm] = useState(false);

  useEffect(() => {
    // Only load billing data if user is admin
    if (isOrgAdmin()) {
      loadBillingData();
    } else {
      setIsLoading(false);
    }
  }, [isOrgAdmin]);

  const loadBillingData = async () => {
    setIsLoading(true);
    try {
      // Load subscription and plans first
      const [subscription, plans] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getPlans()
      ]);
      
      setCurrentSubscription(subscription);
      setAvailablePlans(plans);
      
      // Load invoices regardless of subscription status
      try {
        const invoiceData = await subscriptionService.getInvoices(5);
        setInvoices(invoiceData.invoices);
      } catch (err) {
        console.error('Failed to load invoice data:', err);
        setInvoices([]);
      }

      // Only load usage if user has an active subscription
      if (subscription) {
        try {
          const usage = await subscriptionService.getUsageDetails();
          setUsageDetails(usage);
        } catch (err) {
          console.error('Failed to load usage data:', err);
          // Don't fail completely if usage fails to load
          setUsageDetails(null);
        }
      } else {
        setUsageDetails(null);
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (planId: string) => {
    setIsUpgrading(true);
    try {
      // Get plan name for display
      const targetPlan = availablePlans.find(p => p.id === planId);
      const targetPlanName = targetPlan?.name || 'selected plan';

      // If no active subscription, canceled, or trial → skip validation and go straight to checkout
      // Trial users upgrading to paid plans don't need validation (they have no real usage to compare)
      const isTrialUpgrade = currentSubscription?.plan_name === 'Trial' || currentSubscription?.status === 'trialing';
      if (!currentSubscription || currentSubscription.status === 'canceled' || isTrialUpgrade) {
        const { checkout_url } = await subscriptionService.createCheckoutSession(planId);
        window.location.href = checkout_url;
        return;
      }

      // For active subscriptions, validate if the plan change is allowed
      const validationResult = await subscriptionService.validatePlanChange(planId);

      // Show validation modal
      setValidationModal({
        isOpen: true,
        validation: validationResult.validation,
        targetPlanId: planId,
        targetPlanName: targetPlanName,
      });

      setIsUpgrading(false);
    } catch (error: any) {
      console.error('Failed to validate plan change:', error);
      alert('Failed to validate plan change. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleProceedWithPlanChange = async () => {
    if (!validationModal.targetPlanId) return;
    
    setIsUpgrading(true);
    try {
      const { checkout_url } = await subscriptionService.createCheckoutSession(validationModal.targetPlanId);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to proceed with plan change. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      // Check if user has any usage that might block downgrades
      if (currentSubscription && usageDetails) {
        const hasHighUsage = usageDetails.credits.percentage > 80 ||
                            usageDetails.syncs.percentage > 80 ||
                            usageDetails.stores.percentage > 80;

        if (hasHighUsage) {
          setBillingPortalConfirm(true);
          return;
        }
      }

      await openBillingPortal();
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const openBillingPortal = async () => {
    try {
      const { portal_url } = await subscriptionService.createBillingPortalSession();
      window.open(portal_url, '_blank');
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const getUpgradeRecommendation = () => {
    if (!currentSubscription) return null;
    return subscriptionService.getUpgradeRecommendation(currentSubscription);
  };

    // Check if user is admin - only admins can access billing
  if (!isOrgAdmin()) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="p-4 rounded-full bg-red-100 dark:bg-red-950/30">
          <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Access Denied</h2>
        <p className="text-center text-gray-600 dark:text-gray-400 max-w-md">
          Only organization administrators can access billing and subscription settings. 
          Please contact your admin if you need to make changes to your subscription.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-16 rounded-xl bg-muted"></div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-muted"></div>
          ))}
        </div>
        <div className="h-96 rounded-xl bg-muted"></div>
      </div>
    );
  }

  const upgradeRecommendation = getUpgradeRecommendation();

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center animate-slide-down">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Billing & Subscription</h1>
          <p className="text-muted-foreground">
            Manage your subscription, view usage, and access billing information
          </p>
        </div>
        {currentSubscription && (
          <div className="flex gap-3 items-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-semibold text-sm shadow-sm ${
              currentSubscription.plan_name.toLowerCase() === 'starter'
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200'
                : currentSubscription.plan_name.toLowerCase() === 'creator'
                ? 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20 border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-200'
                : currentSubscription.plan_name.toLowerCase() === 'builder'
                ? 'bg-gradient-to-r from-cyan-50 to-sky-50 dark:from-cyan-950/20 dark:to-sky-950/20 border-cyan-300 dark:border-cyan-700 text-cyan-800 dark:text-cyan-200'
                : currentSubscription.plan_name.toLowerCase() === 'growth mini'
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-200'
                : currentSubscription.plan_name.toLowerCase() === 'bronze'
                ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200'
                : currentSubscription.plan_name.toLowerCase() === 'silver'
                ? 'bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/20 dark:to-gray-950/20 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200'
                : currentSubscription.plan_name.toLowerCase() === 'gold'
                ? 'bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:to-amber-950/20 border-yellow-400 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200'
                : currentSubscription.plan_name.toLowerCase() === 'enterprise'
                ? 'bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20 border-purple-300 dark:border-purple-600 text-purple-800 dark:text-purple-200'
                : currentSubscription.plan_name.toLowerCase() === 'custom'
                ? 'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border-indigo-300 dark:border-indigo-600 text-indigo-800 dark:text-indigo-200'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-200'
            }`}>
              <Crown className="w-4 h-4" />
              <span>{currentSubscription.plan_name} Plan</span>
            </div>
            <Button 
              variant="outline" 
              onClick={handleManageBilling} 
              disabled={!hasPermission('billing', 'edit')}
              className="gap-2"
              title={!hasPermission('billing', 'edit') ? "You don't have permission to manage billing" : undefined}
            >
              <Settings className="w-4 h-4" />
              {!hasPermission('billing', 'edit') ? 'No Permission' : 'Manage Billing'}
            </Button>
          </div>
        )}
      </div>

      {/* Cancellation Warning Banner - only show if canceling but not yet fully canceled */}
      {currentSubscription?.cancel_at_period_end && currentSubscription?.status !== 'canceled' && (
        <WarningBanner
          variant="warning"
          title="Subscription Ending Soon"
          message={`Your ${currentSubscription.plan_name} subscription will end on ${new Date(currentSubscription.current_period_end).toLocaleDateString()}. Reactivate to keep your access.`}
          action={{
            label: 'Reactivate Subscription',
            onClick: handleManageBilling,
          }}
        />
      )}

      {/* Upgrade Recommendation Alert */}
      {upgradeRecommendation && !currentSubscription?.cancel_at_period_end && (
        <WarningBanner
          variant="premium"
          title="Upgrade Recommended"
          message={upgradeRecommendation.reason}
          action={{
            label: `View ${upgradeRecommendation.recommendedPlan} Plan`,
            onClick: () => setSelectedTab('plans'),
          }}
        />
      )}

      {/* Usage Limit Banner */}
      {currentSubscription && (
        <UsageLimitBanner
          currentUsage={currentSubscription.current_usage}
          usageLimit={currentSubscription.usage_limit}
          usagePercentage={currentSubscription.usage_percentage}
          planName={currentSubscription.plan_name}
          onUpgrade={() => setSelectedTab('plans')}
        />
      )}

      {/* Current Subscription Overview */}
      {currentSubscription && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 animate-slide-up">
          {/* Usage Progress */}
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row justify-between items-center pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Credit Usage</CardTitle>
              <Zap className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentSubscription.current_usage.toLocaleString()}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                of {currentSubscription.usage_limit.toLocaleString()} credits
              </p>
              <Progress 
                value={currentSubscription.usage_percentage} 
                className="mb-2 h-2"
                style={{
                  '--progress-foreground': subscriptionService.getUsageStatusColor(currentSubscription.usage_percentage)
                } as React.CSSProperties}
              />
              <p className="text-xs text-muted-foreground">
                {(currentSubscription.usage_limit - currentSubscription.current_usage).toLocaleString()} remaining
              </p>
            </CardContent>
          </Card>

          {/* Sync Usage */}
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row justify-between items-center pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Monthly Syncs</CardTitle>
              <RefreshCw className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentSubscription.sync_limit >= 999999 ? '∞' : currentSubscription.current_month_syncs}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {currentSubscription.sync_limit >= 999999 ? 'Unlimited syncs' : `of ${currentSubscription.sync_limit} syncs this month`}
              </p>
              {currentSubscription.sync_limit >= 999999 ? (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded-md px-3 py-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>No limits on your plan</span>
                </div>
              ) : (
                <>
                  <Progress
                    value={(currentSubscription.current_month_syncs / currentSubscription.sync_limit) * 100}
                    className="mb-2 h-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    {currentSubscription.sync_remaining} remaining
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Connectors Usage */}
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row justify-between items-center pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Connectors</CardTitle>
              <Plug className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentSubscription.current_stores}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {currentSubscription.store_limit >= 999999 ? 'Unlimited connectors' : `of ${currentSubscription.store_limit} connectors allowed`}
              </p>
              {currentSubscription.store_limit >= 999999 ? (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded-md px-3 py-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>No limits on your plan</span>
                </div>
              ) : (
                <>
                  <Progress
                    value={(currentSubscription.current_stores / currentSubscription.store_limit) * 100}
                    className="mb-2 h-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    {currentSubscription.store_limit - currentSubscription.current_stores} available
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Promotional Banner - Only show for new subscriptions */}
      {PROMO_CONFIG.enabled && (!currentSubscription || currentSubscription.plan_name === 'Trial') && (
        <div
          className="mb-6 relative overflow-hidden rounded-xl border-2 border-orange-300 dark:border-orange-600 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 dark:from-orange-950/40 dark:via-amber-950/40 dark:to-yellow-950/40 cursor-pointer hover:shadow-lg hover:scale-[1.01] transition-all duration-200"
          onClick={() => setSelectedTab('plans')}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTIgMGEyIDIgMCAxIDAgNCAwYTIgMiAwIDEgMCAtNCAwIiBmaWxsPSJyZ2JhKDI1MSwgMTQ2LCA2MCwgMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-50"></div>
          <div className="relative px-6 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 p-3 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl shadow-lg">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-full uppercase tracking-wide flex items-center gap-1">
                      <Flame className="w-3 h-3" />
                      {PROMO_CONFIG.title}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-orange-800 dark:text-orange-200">
                    {PROMO_CONFIG.description}
                  </h3>
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    Subscribe now and receive double the credits on your first billing cycle
                  </p>
                  <p className="text-xs text-orange-500 dark:text-orange-500 mt-1 italic">
                    * This offer applies to new subscriptions only. Existing subscribers are not eligible.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="px-4 py-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg shadow-lg">
                  <div className="text-2xl font-black text-white">{PROMO_CONFIG.badge}</div>
                  <div className="text-xs text-orange-100">Click to view plans</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="animate-slide-up" style={{animationDelay: '100ms'}}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="plans" className="gap-2">
            <Crown className="w-4 h-4" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2">
            <Receipt className="w-4 h-4" />
            Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {currentSubscription ? (
            <>
              {/* Current Plan Details */}
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex gap-2 items-center">
                    <Crown className="w-5 h-5 text-primary" />
                    Current Plan: {currentSubscription.plan_name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Monthly Price</span>
                      <span className="font-medium">
                        {subscriptionService.formatCurrency(currentSubscription.monthly_price)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={
                        currentSubscription.status === 'canceled'
                          ? 'destructive'
                          : currentSubscription.cancel_at_period_end
                          ? 'destructive'
                          : currentSubscription.status === 'active'
                          ? 'outline'
                          : 'secondary'
                      }>
                        {currentSubscription.status === 'canceled' ? (
                          <>
                            <XCircle className="mr-1 w-3 h-3" />
                            Canceled
                          </>
                        ) : currentSubscription.cancel_at_period_end ? (
                          <>
                            <AlertTriangle className="mr-1 w-3 h-3" />
                            Canceling
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-1 w-3 h-3" />
                            {currentSubscription.status}
                          </>
                        )}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        {currentSubscription.status === 'canceled'
                          ? 'Ended On'
                          : currentSubscription.cancel_at_period_end
                          ? 'Access Until'
                          : 'Next Billing'}
                      </span>
                      <span className="font-medium">
                        {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Button 
                      variant="outline" 
                      className="flex-col gap-2 p-4 h-auto"
                      onClick={() => setSelectedTab('plans')}
                    >
                      <ArrowUpCircle className="w-6 h-6 text-primary" />
                      <span className="font-medium">Upgrade Plan</span>
                      <span className="text-xs text-muted-foreground">Get more features</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-col gap-2 p-4 h-auto"
                      onClick={() => setSelectedTab('invoices')}
                    >
                      <DollarSign className="w-6 h-6 text-primary" />
                      <span className="font-medium">View Invoices</span>
                      <span className="text-xs text-muted-foreground">Get your invoices</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-col gap-2 p-4 h-auto"
                      onClick={handleManageBilling}
                      disabled={!hasPermission('billing', 'edit')}
                      title={!hasPermission('billing', 'edit') ? "You don't have permission to manage billing" : undefined}
                    >
                      <Settings className="w-6 h-6 text-primary" />
                      <span className="font-medium">Manage Billing</span>
                      <span className="text-xs text-muted-foreground">Update payment info</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            /* No Subscription State */
            <Card className="p-8 text-center card-enhanced">
              <CardContent className="space-y-4">
                <div className="flex justify-center items-center mx-auto w-16 h-16 rounded-full bg-primary/10">
                  <CreditCard className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">No Active Subscription</h3>
                  <p className="text-muted-foreground">
                    Choose a plan to start processing your images with advanced AI features
                  </p>
                </div>
                <Button 
                  size="lg" 
                  className="gap-2"
                  onClick={() => setSelectedTab('plans')}
                >
                  <Sparkles className="w-4 h-4" />
                  View Plans
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="plans" className="space-y-6">
          {/* Plan Header */}
          <div className="mt-8 mb-6 space-y-4 text-center">
            <h2 className="text-3xl font-bold">Choose Your Plan</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Transform your product images with AI-powered processing. All plans include full access to AI features.
            </p>
          </div>

          {/* Current Subscription Status */}
          {!currentSubscription ? (
            <div className="mb-4">
              <div className="flex gap-3 items-center p-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
                <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 bg-amber-100 rounded-full dark:bg-amber-900">
                  <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">No Active Subscription</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Choose a plan below to start processing images</p>
                </div>
              </div>
            </div>
          ) : currentSubscription?.plan_name === 'Trial' && (
            <div className="mb-4">
              <div className="flex gap-3 items-center p-3 rounded-lg border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
                <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 bg-blue-100 rounded-full dark:bg-blue-900">
                  <Star className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Free Trial Active</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {currentSubscription?.usage_percentage ?
                      `${Math.max(0, 100 - currentSubscription.usage_percentage).toFixed(0)} credits remaining` :
                      '100 free credits to explore TinyScale Studio'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plan Filter Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {[
              { id: 'all', label: 'All Plans', icon: <BarChart3 className="w-4 h-4" /> },
              { id: 'individuals', label: 'Individuals', icon: <Zap className="w-4 h-4" /> },
              { id: 'small', label: 'Small Business', icon: <TrendingUp className="w-4 h-4" /> },
              { id: 'growing', label: 'Growing Business', icon: <Star className="w-4 h-4" /> },
              { id: 'enterprise', label: 'Enterprise', icon: <Crown className="w-4 h-4" /> },
            ].map((filter) => (
              <Button
                key={filter.id}
                variant={planFilter === filter.id ? "default" : "outline"}
                size="sm"
                className={`gap-2 ${planFilter === filter.id ? 'bg-gradient-primary' : ''}`}
                onClick={() => setPlanFilter(filter.id)}
              >
                {filter.icon}
                {filter.label}
              </Button>
            ))}
          </div>

          {/* Plans Grid */}
          {(() => {
            // Define which plans belong to which filter
            const filterPlanMap: Record<string, string[]> = {
              all: ['Starter', 'Creator', 'Builder', 'Growth Mini', 'Bronze', 'Silver', 'Gold', 'Enterprise'],
              individuals: ['Starter', 'Creator'],
              small: ['Builder', 'Growth Mini'],
              growing: ['Bronze', 'Silver'],
              enterprise: ['Gold', 'Enterprise'],
            };

            // Get plan icon based on tier
            const getPlanIcon = (planName: string) => {
              switch (planName) {
                case 'Starter': return <Zap className="w-6 h-6 text-white" />;
                case 'Creator': return <TrendingUp className="w-6 h-6 text-white" />;
                case 'Builder': return <Settings className="w-6 h-6 text-white" />;
                case 'Growth Mini': return <BarChart3 className="w-6 h-6 text-white" />;
                case 'Bronze': return <Star className="w-6 h-6 text-white" />;
                case 'Silver': return <Zap className="w-6 h-6 text-white" />;
                case 'Gold': return <Crown className="w-6 h-6 text-white" />;
                case 'Enterprise': return <Sparkles className="w-6 h-6 text-white" />;
                default: return <Zap className="w-6 h-6 text-white" />;
              }
            };

            // Format store/sync limits for display (∞ for unlimited)
            const formatLimit = (limit: number) => limit >= 999999 ? '∞' : limit.toString();

            // Format credits display
            const formatCredits = (limit: number) => {
              if (limit >= 10000) return `${(limit / 1000).toFixed(0)}k`;
              if (limit >= 1000) return `${(limit / 1000).toFixed(1).replace(/\.0$/, '')}k`;
              return limit.toLocaleString();
            };

            // Filter plans based on selected filter
            const filteredPlans = availablePlans.filter(
              p => p.name !== 'Trial' && p.name !== 'Custom' && filterPlanMap[planFilter]?.includes(p.name)
            );

            // Get the most popular plan for each category
            const getCategoryPopularPlan = (filter: string): string | null => {
              switch (filter) {
                case 'individuals': return 'Creator';
                case 'small': return 'Growth Mini';
                case 'growing': return 'Bronze';
                case 'enterprise': return 'Gold';
                case 'all': return null; // No single popular when showing all
                default: return null;
              }
            };

            const categoryPopularPlan = getCategoryPopularPlan(planFilter);

            // Check if user is eligible for promo (new subscriptions only)
            const isPromoEligible = PROMO_CONFIG.enabled &&
              (!currentSubscription || currentSubscription.plan_name === 'Trial');

            return (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredPlans.map((plan) => {
                  const isPopular = categoryPopularPlan === plan.name;
                  const isCurrent = currentSubscription?.plan_name === plan.name &&
                                    currentSubscription?.status !== 'canceled';
                  const promoCredits = isPromoEligible ? plan.usage_limit * PROMO_CONFIG.bonusMultiplier : plan.usage_limit;

                  return (
                    <div key={plan.id} className="relative">
                      {/* Popular Badge */}
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 z-20 transform -translate-x-1/2">
                          <div className="px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-2 border-white shadow-lg">
                            <div className="flex gap-1 items-center">
                              <Crown className="w-3 h-3" />
                              Popular
                            </div>
                          </div>
                        </div>
                      )}

                      <Card
                        className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] h-full ${
                          isCurrent
                            ? 'border-2 border-primary shadow-2xl bg-gradient-to-br from-primary/5 to-accent/5'
                            : isPopular
                            ? 'border-2 border-primary/50 shadow-xl bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20'
                            : 'border border-border hover:border-border-hover shadow-lg hover:shadow-xl'
                        }`}
                      >
                        {/* Promo Badge */}
                        {isPromoEligible && !isCurrent && (
                          <div className="absolute top-4 left-4 z-10">
                            <Badge className="text-orange-800 bg-orange-100 border-orange-200 dark:text-orange-200 dark:bg-orange-900/50 dark:border-orange-700">
                              <Gift className="mr-1 w-3 h-3" />
                              2X Credits
                            </Badge>
                          </div>
                        )}

                        {/* Current Plan Badge */}
                        {isCurrent && (
                          <div className="absolute top-4 right-4 z-10">
                            <Badge className="text-green-800 bg-green-100 border-green-200">
                              <CheckCircle className="mr-1 w-3 h-3" />
                              Current
                            </Badge>
                          </div>
                        )}

                        {/* Card Header */}
                        <CardHeader className="pt-6 pb-4 text-center">
                          {/* Plan Icon */}
                          <div className="flex justify-center items-center mx-auto mb-4 w-14 h-14 rounded-full bg-gradient-primary">
                            {getPlanIcon(plan.name)}
                          </div>

                          <CardTitle className="mb-2 text-xl font-bold">{plan.name}</CardTitle>

                          {/* Pricing */}
                          <div className="space-y-1">
                            <div className="text-3xl font-bold">
                              {subscriptionService.formatCurrency(plan.monthly_price)}
                              <span className="text-base font-normal text-muted-foreground">/mo</span>
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="px-5 pb-6 space-y-5">
                          {/* Key Metrics */}
                          <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-muted/30 text-center">
                            <div>
                              {isPromoEligible ? (
                                <>
                                  <div className="text-lg font-bold text-primary">
                                    <span className="line-through text-muted-foreground text-sm mr-1">{formatCredits(plan.usage_limit)}</span>
                                    <span className="text-orange-600 dark:text-orange-400">{formatCredits(promoCredits)}</span>
                                  </div>
                                  <div className="text-xs text-orange-600 dark:text-orange-400 font-semibold">Credits (2X!)</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-lg font-bold text-primary">{formatCredits(plan.usage_limit)}</div>
                                  <div className="text-xs text-muted-foreground">Credits</div>
                                </>
                              )}
                            </div>
                            <div>
                              <div className="text-lg font-bold text-primary">{formatLimit(plan.store_limit)}</div>
                              <div className="text-xs text-muted-foreground">Connectors</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-primary">{formatLimit(plan.sync_limit)}</div>
                              <div className="text-xs text-muted-foreground">Syncs</div>
                            </div>
                          </div>

                          {/* Features */}
                          <div className="space-y-2">
                            {subscriptionService.getPlanHighlights(plan.name).slice(0, 4).map((highlight, idx) => (
                              <div key={idx} className="flex gap-2 items-start text-sm">
                                <CheckCircle className="flex-shrink-0 w-4 h-4 mt-0.5 text-green-500" />
                                <span className="text-muted-foreground">{highlight}</span>
                              </div>
                            ))}
                          </div>

                          {/* Action Button */}
                          <Button
                            className={`w-full gap-2 h-11 text-sm font-semibold transition-all duration-200 ${
                              isPopular
                                ? 'bg-gradient-primary hover:shadow-lg hover:scale-105'
                                : isCurrent
                                ? 'text-green-800 bg-green-100 border-green-200 hover:bg-green-200'
                                : ''
                            }`}
                            variant={isCurrent ? "outline" : isPopular ? "default" : "outline"}
                            disabled={!hasPermission('billing', 'edit') || isUpgrading || isCurrent}
                            onClick={() => handleUpgrade(plan.id)}
                            title={!hasPermission('billing', 'edit') ? "You don't have permission to change billing plans" : undefined}
                          >
                            {isCurrent ? (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                Current Plan
                              </>
                            ) : (
                              <>
                                {(() => {
                                  const effectiveCurrentPlan = currentSubscription?.status === 'canceled'
                                    ? null
                                    : currentSubscription?.plan_name || null;
                                  const buttonText = subscriptionService.getButtonText(effectiveCurrentPlan, plan.name, isCurrent);
                                  const isDowngrade = buttonText === 'Downgrade';
                                  const IconComponent = isDowngrade ? ArrowDownCircle :
                                                       buttonText === 'Upgrade Now' ? ArrowUpCircle :
                                                       Sparkles;
                                  return (
                                    <>
                                      <IconComponent className="w-4 h-4" />
                                      {buttonText}
                                    </>
                                  );
                                })()}
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Custom/Enterprise+ Plan - Separate Section */}
          {availablePlans.find(plan => plan.name === 'Custom') && (
            <div className="mt-8">
              <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20">
                <CardContent className="flex flex-col items-center justify-center gap-4 py-8 md:flex-row md:justify-between">
                  <div className="text-center md:text-left">
                    <div className="flex gap-2 items-center justify-center md:justify-start mb-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      <h3 className="text-xl font-bold">Custom / Enterprise+</h3>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Need more than 500k credits? Custom credit allocation, dedicated account manager, custom integrations & SLA.
                    </p>
                  </div>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
                    onClick={() => window.location.href = 'mailto:support@tinyscale.ai?subject=Custom Enterprise Plan Inquiry'}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Additional Information */}
          <div className="mt-8 space-y-4 text-center">
            <div className="flex flex-wrap gap-6 justify-center items-center text-sm text-muted-foreground">
              <div className="flex gap-2 items-center">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex gap-2 items-center">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Secure payments</span>
              </div>
              <div className="flex gap-2 items-center">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>All AI features included</span>
              </div>
            </div>
          </div>
        </TabsContent>


        <TabsContent value="invoices" className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>Recent Invoices</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleManageBilling} 
                disabled={!hasPermission('billing', 'view')}
                className="gap-2"
                title={!hasPermission('billing', 'view') ? "You don't have permission to view billing" : undefined}
              >
                <ExternalLink className="w-4 h-4" />
                {!hasPermission('billing', 'view') ? 'No Permission' : 'View All'}
              </Button>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div 
                      key={invoice.id}
                      className="flex justify-between items-center p-4 rounded-lg border"
                    >
                      <div className="space-y-1">
                        <div className="font-medium">
                          {subscriptionService.formatCurrency(invoice.amount_paid, invoice.currency)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex gap-3 items-center">
                        <Badge 
                          variant={invoice.status === 'paid' ? 'outline' : 'secondary'}
                          className={invoice.status === 'paid' ? 'text-green-700 dark:text-green-300 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-950/30' : ''}
                        >
                          {invoice.status}
                        </Badge>
                        {invoice.invoice_url && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => window.open(invoice.invoice_url, '_blank')}
                            className="gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No invoices available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Change Validation Modal */}
      <PlanChangeValidationModal
        isOpen={validationModal.isOpen}
        onClose={() => setValidationModal({
          isOpen: false,
          validation: null,
          targetPlanId: null,
          targetPlanName: null,
        })}
        validation={validationModal.validation}
        targetPlanName={validationModal.targetPlanName || undefined}
        onProceed={handleProceedWithPlanChange}
        isProcessing={isUpgrading}
      />

      {/* Billing Portal High Usage Confirmation Modal */}
      <Dialog open={billingPortalConfirm} onOpenChange={setBillingPortalConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              High Usage Warning
            </DialogTitle>
            <DialogDescription>
              You have high usage in your current plan. Downgrading may not be possible.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            {usageDetails && (
              <>
                <div className="flex justify-between text-sm">
                  <span>Credits:</span>
                  <span className={usageDetails.credits.percentage > 80 ? 'text-amber-500 font-medium' : ''}>
                    {usageDetails.credits.percentage.toFixed(1)}% used
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Syncs:</span>
                  <span className={usageDetails.syncs.percentage > 80 ? 'text-amber-500 font-medium' : ''}>
                    {usageDetails.syncs.percentage.toFixed(1)}% used
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Connectors:</span>
                  <span className={usageDetails.stores.percentage > 80 ? 'text-amber-500 font-medium' : ''}>
                    {usageDetails.stores.percentage.toFixed(1)}% used
                  </span>
                </div>
              </>
            )}
            <p className="text-sm text-muted-foreground mt-4">
              Note: You won't be able to downgrade if your usage exceeds the lower plan's limits.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingPortalConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              setBillingPortalConfirm(false);
              openBillingPortal();
            }}>
              Continue to Billing Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};